import { Button, useDisclosure } from "@heroui/react";
import { useEffect, useState } from "react";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { FaPlus, FaPencil, FaLink } from "react-icons/fa6";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { InvitationTypeSelectionModal } from "@/app/components/modals/invitation/invitationTypeSelectionModal";
import { useNavigate } from "react-router-dom";
import CopyToClipboard from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import { api } from "@/api";
import { Invitation } from "@/api/invitations";
import useErrorHandling from "@/hooks/useErrorHandling";

export function InvitationsPage() {
  const handleError = useErrorHandling();
  const [invitations, setInvitations] = useState<Invitation[]>([]);

  const navigate = useNavigate();
  const typeSelectionModalDisclosure = useDisclosure();
  const fullscreenDisclosure = useDisclosure();

  useEffect(() => {
    const loadInvitations = async () => {
      try {
        const response = await api.invitations.list();
        setInvitations(response.data);
      } catch (err: any) {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      }
    };

    loadInvitations();
  }, [handleError]);

  const handleSelectType = (
    type: "existing_renter" | "new_renter" | "cancel",
  ) => {
    if (type === "cancel") return;
    if (type === "existing_renter") {
      navigate("/dashboard/invitations/existing/create");
    } else if (type === "new_renter") {
      navigate("/dashboard/invitations/new/create");
    }
  };

  const fields = [
    {
      key: "type",
      label: "Тип",
      type: ColumnType.Custom,
      sortable: true,
      render(_value: any, row: Invitation) {
        return row.type === "existing_renter"
          ? "Существующий арендатор"
          : "Новый арендатор";
      },
    },
    {
      key: "renter",
      label: "Арендатор",
      type: ColumnType.Custom,
      render(_value: any, row: Invitation) {
        if (row.type === "existing_renter" && row.renter_name) {
          return row.renter_name;
        } else if (row.type === "new_renter" && row.renter_name) {
          return row.renter_name;
        }
        return "н/д";
      },
    },
    {
      key: "price",
      label: "Цена",
      type: ColumnType.Custom,
      sortable: true,
      render(_value: any, row: Invitation) {
        if (row.price !== undefined && row.price !== null) {
          return `${row.price} ₽`;
        }
        return "н/д";
      },
    },
    {
      key: "start_date",
      label: "Дата начала",
      type: ColumnType.Custom,
      sortable: true,
      render(_value: any, row: Invitation) {
        if (row.start_date) {
          try {
            const date = new Date(row.start_date);
            return date.toLocaleDateString("ru-RU");
          } catch {
            return row.start_date;
          }
        }
        return "н/д";
      },
    },
    {
      key: "end_date",
      label: "Дата окончания",
      type: ColumnType.Custom,
      sortable: true,
      render(_value: any, row: Invitation) {
        if (row.end_date) {
          try {
            const date = new Date(row.end_date);
            return date.toLocaleDateString("ru-RU");
          } catch {
            return row.end_date;
          }
        }
        return "н/д";
      },
    },
    {
      key: "actions",
      label: "Действия",
      type: ColumnType.Custom,
      render(_value: any, row: Invitation) {
        const invitationLink = `https://app.elasticspace.app/invitations/${row.opaque_id}`;
        return (
          <div className="flex gap-2">
            <CopyToClipboard
              text={invitationLink}
              onCopy={() => {
                toast.success("Ссылка скопирована в буфер обмена!");
              }}>
              <Button isIconOnly size="sm" variant="bordered">
                <FaLink />
              </Button>
            </CopyToClipboard>
            <Button
              isIconOnly
              size="sm"
              variant="bordered"
              onPress={() => {
                if (row.type === "existing_renter") {
                  navigate(`/dashboard/invitations/existing/${row.id}/edit`);
                } else {
                  navigate(`/dashboard/invitations/new/${row.id}/edit`);
                }
              }}>
              <FaPencil />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <InvitationTypeSelectionModal
        disclosure={typeSelectionModalDisclosure}
        onSelectType={handleSelectType}
      />

      <div className="flex flex-row flex-wrap gap-4 w-full">
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
          <div className="flex flex-row justify-between w-full items-center mb-4">
            <div className="flex flex-row gap-4 items-center">
              <span className="font-medium">Приглашения</span>
            </div>
            <div className="flex flex-row gap-4 items-center">
              <Button
                startContent={<FaPlus />}
                color="primary"
                variant="solid"
                className="max-w-fit"
                onPress={() => typeSelectionModalDisclosure.onOpen()}>
                Пригласить
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-2 mb-2">
            <TableBuilder
              fullscreenDisclosure={fullscreenDisclosure}
              displayOptions={false}
              removeWrapper
              rowsPerPage={10}
              columns={fields}
              data={invitations}
              defaultSortDescriptor={{
                column: "start_date",
                direction: "descending",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
